import { memo } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import type { ClientResponseDTO } from "@/features/clients/types/client.types";
import type { ContractResponseDTO } from "@/features/contracts/types/contract.types";
import { ClientPickerField } from "../../ClientPickerField";
import { ContractPickerField } from "../../ContractPickerField";
import { ParentContractField } from "../../ParentContractField";
import type { RentalCreateFormSchema } from "../../rental-create-schema";
import { StepHeading } from "./StepHeading";

interface StepClientContractProps {
  form: UseFormReturn<RentalCreateFormSchema>;
  selectedClientLabel: string | null;
  selectedContractLabel: string | null;
  parentContractLabel: string | null;
  onClientSelect: (client: ClientResponseDTO) => void;
  onClientCreateOpen: () => void;
  onClientClear: () => void;
  onContractSelect: (contract: ContractResponseDTO) => void;
  onContractCreateOpen: () => void;
  onContractClear: () => void;
  onParentContractSelect: (contract: ContractResponseDTO) => void;
  onParentContractClear: () => void;
}

/**
 * Etapa 2 — cliente e contrato. Reaproveita os pickers de busca/cadastro
 * inline já existentes. Cliente e contrato podem ser reusados (busca) ou
 * criados no mesmo fluxo (dialogs disparados pela page).
 */
export const StepClientContract = memo(function StepClientContract({
  form,
  selectedClientLabel,
  selectedContractLabel,
  parentContractLabel,
  onClientSelect,
  onClientCreateOpen,
  onClientClear,
  onContractSelect,
  onContractCreateOpen,
  onContractClear,
  onParentContractSelect,
  onParentContractClear,
}: StepClientContractProps) {
  const errors = form.formState.errors;
  const selectedClientId = useWatch({
    control: form.control,
    name: "selectedClientId",
  });
  const newClient = useWatch({ control: form.control, name: "newClient" });
  const selectedContractId = useWatch({
    control: form.control,
    name: "selectedContractId",
  });
  const newContract = useWatch({ control: form.control, name: "newContract" });
  const parentContractId = useWatch({
    control: form.control,
    name: "parentContractId",
  });

  return (
    <div className="space-y-6">
      <StepHeading
        title="Cliente e contrato"
        description="Busque um cadastro existente ou crie um novo na hora."
      />

      <ClientPickerField
        selectedClientId={selectedClientId}
        selectedClientLabel={selectedClientLabel}
        newClient={newClient}
        onSelectExisting={onClientSelect}
        onCreateNew={onClientCreateOpen}
        onClear={onClientClear}
        error={errors.selectedClientId?.message}
      />
      <ContractPickerField
        selectedContractId={selectedContractId}
        selectedContractLabel={selectedContractLabel}
        newContract={newContract}
        onSelectExisting={onContractSelect}
        onCreateNew={onContractCreateOpen}
        onClear={onContractClear}
        error={errors.selectedContractId?.message}
      />
      <ParentContractField
        parentContractId={parentContractId}
        parentContractLabel={parentContractLabel}
        onSelect={onParentContractSelect}
        onClear={onParentContractClear}
      />
    </div>
  );
});
